"use client";

import Helper from "@/utils/helper";
import { useEffect, useMemo, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import useGetFilterProduct from "@/lib/hooks/useGetFilterProduct";
import useTranslationApi from "@/lib/hooks/useTranslationApi";

import { BrandJson } from "@/types/Brand.type";
import { FilterProduct, ProductTagJson } from "@/types/Product.type";

import RangeSlider from "./RangeSlider";
import LoadMore from "@/components/LoadMore";
import { LoadingIcon } from "@/components/icons";
import ProductFilterRate from "./ProductFilterRate";
import ProductFilterItem from "./ProductFilterItem";
import ChevronDownIcon from "@/components/icons/ChevronDownIcon";
import FilterSelect from "~components/product/ProductFilterSelect";
interface ObjectWithArrayValues {
	[key: string]: string | string[] | number[];
}
type Props = {
	is_brand?: boolean;
	is_size?: boolean;
	is_rate?: boolean;
	is_price?: boolean;
	onLoad?: (isLoading: boolean) => void;
	collectionHandle?: string;
	defaultFilter: FilterProduct;
};

const translate = {
	filter_brand_title: "filter_brand_title",
	filter_price_title: "filter_price_title",
	filter_rating_title: "filter_rating_title",
	filter_title: "filter_title",
};

const ProductFilter = ({
	is_brand = true,
	is_size = true,
	is_rate = true,
	is_price = true,
	onLoad,
	collectionHandle,
	defaultFilter,
}: Props) => {
	const pathname = usePathname();
	const router = useRouter();
	const params = defaultFilter;
	const [selectedItems, setSelectedItems] = useState<number[]>([]);

	const { t } = useTranslationApi(Object.values(translate));
	const {
		isLoading,
		isLoadingBrand,
		setDefaultBrandFilter,
		valueFilters,
		setDefaultSizeFilter,
		isLoadingSize,
	} = useGetFilterProduct({ collectionHandle: collectionHandle || "" });

	const [filterState, setFilterState] = useState<FilterProduct>(
		{} as FilterProduct
	);
	const filterItems: {
		name: keyof FilterProduct;
		title: string;
		show: boolean;
		children: JSX.Element;
	}[] = useMemo(() => {
		return [
			{
				name: "brand",
				title: t(translate.filter_brand_title),
				show: is_brand,
				children: (
					<LoadMore
						onLoadMore={() =>
							setDefaultBrandFilter((pre) => ({
								...pre,
								page: Number.parseInt(pre.page?.toString() || "1") + 1,
							}))
						}
						total={valueFilters.brands.total}
						totalCurrent={valueFilters.brands.items.length}
						loading={isLoadingBrand}>
						<FilterSelect
							onChange={(data) => {
								handerPushSearchParams({
									brand: data.length
										? data.map((item) => item?.handle).join(",")
										: undefined,
								});
							}}
							defaultValue={Helper.handleFilterActive<BrandJson>(
								filterState?.brand,
								valueFilters.brands.items,
								"handle"
							)}
							isMutiple
							dataSource={valueFilters.brands.items}
							renderItem={(item, state) => {
								return (
									<ProductFilterItem
										active={state.some((it) => it.handle === item.handle)}>
										<div className="hover:text-red-500 ml-[5px]">
											{item.title}
										</div>
									</ProductFilterItem>
								);
							}}
						/>
					</LoadMore>
				),
			},
			{
				name: "price_max",
				title: t(translate.filter_price_title),
				show: is_price,
				children: (
					<>
						{isLoading ? (
							<LoadingIcon />
						) : (
							<RangeSlider
								initialMin={valueFilters.priceMin}
								initialMax={valueFilters.priceMax}
								min={filterState.price_min || valueFilters.priceMin}
								max={filterState.price_max || valueFilters.priceMax}
								step={50000}
								onChange={({ priceMax, priceMin }) => {
									handerPushSearchParams({
										price_max: priceMax,
										price_min: priceMin,
									});
								}}
								className="mt-2"
							/>
						)}
					</>
				),
			},
			{
				name: "tags",
				title: "Size",
				show: is_size,
				children: (
					<LoadMore
						onLoadMore={() =>
							setDefaultSizeFilter((pre) => ({
								...pre,
								page: Number.parseInt(pre.page.toString()) + 1,
							}))
						}
						total={valueFilters.sizes.total}
						totalCurrent={valueFilters.sizes.items.length}
						loading={isLoadingSize}>
						<FilterSelect
							onChange={(data) => {
								handerPushSearchParams({
									tags: data.length
										? data.map((item) => item?.code).join(",")
										: undefined,
								});
							}}
							defaultValue={Helper.handleFilterActive<ProductTagJson>(
								filterState?.tags,
								valueFilters.sizes.items,
								"code"
							)}
							isMutiple
							dataSource={valueFilters.sizes.items}
							renderItem={(item, state) => {
								return (
									<ProductFilterItem
										active={state.some((it) => it.code === item.code)}>
										<div className="hover:text-red-500 ml-[5px]">
											{item.name}
										</div>
									</ProductFilterItem>
								);
							}}
						/>
					</LoadMore>
				),
			},
			{
				name: "rating",
				title: t(translate.filter_rating_title),
				show: is_rate,
				children: (
					<ProductFilterRate
						indexActive={filterState.rating || 0}
						onChange={(rate) => {
							handerPushSearchParams({
								rating: rate || undefined,
							});
						}}
					/>
				),
			},
		];
	}, [
		JSON.stringify(valueFilters),
		JSON.stringify(filterState),
		isLoadingBrand,
		isLoadingSize,
	]);

	const toggleSelection = (item: number) => {
		const isSelected = selectedItems.includes(item);
		if (isSelected) {
			setSelectedItems(
				selectedItems.filter((selectedItem) => selectedItem !== item)
			);
		} else {
			setSelectedItems([...selectedItems, item]);
		}
	};
	function objectToSearchParamsValue(obj: ObjectWithArrayValues): {
		[key: string]: string;
	} {
		const processedObj: { [key: string]: string } = {};
		for (const key in obj) {
			if (Array.isArray(obj[key])) {
				processedObj[key] = (obj[key] as (string | number)[]).join(",");
			} else {
				processedObj[key] = obj[key] ? obj[key].toString() : "";
			}
		}
		return processedObj;
	}

	function objectToSearchParams(obj: any): string {
		return Object.keys(obj)
			.filter(
				(key) => obj[key] !== undefined && obj[key] !== null && obj[key] !== ""
			)
			.map((key) => `${encodeURIComponent(key)}=${obj[key]}`)
			.join("&");
	}
	function handerPushSearchParams(
		filter: Omit<FilterProduct, "page" | "store_id">
	) {
		let query: any = {
			...filterState,
			...filter,
			page: 1,
		};
		setFilterState(query);
		const valueSearch = objectToSearchParams(
			objectToSearchParamsValue({ ...query })
		);
		const queryS = valueSearch ? `?${valueSearch}` : "";
		router.push(`${pathname}${queryS}`, { scroll: false });
		onLoad?.(true);
	}

	useEffect(() => {
		// set filter mặc định và ép các kiểu number theo params
		const defaultFilter: FilterProduct = {
			...params,
			rating: Number(params.rating) || undefined,
			price_max: Number(params.price_max) || undefined,
			price_min: Number(params.price_min) || undefined,
		};
		setFilterState(defaultFilter);
	}, [JSON.stringify(params)]);

	useEffect(() => {
		// khi có filter defauil thì mở coolapse đó ( khi co fiter moi hoac dong tru page )
		const listCol = filterItems.map((item) => item.name);
		let res: number[] = [];
		listCol.forEach((lis, index) => {
			const data = filterState[lis];
			if (data) {
				res.push(index);
			}
		});
		setSelectedItems(res);
	}, [JSON.stringify(filterState)]);

	return (
		<div className="shopproductfilter__wrapper text-base text-gray-500 w-full">
			<h3 className="text-sm text-gray-400 my-3">
				{t(translate.filter_title)}
			</h3>
			{filterItems
				.filter((item) => item.show)
				.map((item, index) => {
					return (
						<div key={index}>
							<div
								className="cursor-pointer relative flex items-center justify-between text-base text-gray-500 mb-3 leading-[17px]"
								onClick={() => {
									toggleSelection(index);
								}}>
								<span className="font-semibold leading-[17px]">
									{item.title}
								</span>
								<ChevronDownIcon className="min-w-[14px] w-[14px] h-[14px]" />
							</div>

							<div
								className={`${
									selectedItems.includes(index)
										? "max-h-[320px] border-b border-b-gray-300 pb-[7px] mb-[7px] opacity-100"
										: "max-h-0 opacity-0"
								} transition-all duration-300 overflow-auto`}>
								{item.children}
							</div>
						</div>
					);
				})}
		</div>
	);
};

export default ProductFilter;
